/**
 * App version + update check (J-3616).
 *
 * The service worker serves the app shell cache-first, so an installed app keeps running the
 * bundle it loaded even after a new one is deployed. A release then fails in confusing ways: the
 * first live service-token mint (D-028a) hit E849 twice because the phone was still on the
 * previous bundle. This module makes the running version visible and brings the app up to date:
 *
 *  - VERSION is injected at build time (build.mjs): the git commit plus a hash of the app sources.
 *    build.mjs writes the same hash into the sw.js cache name, so a changed app always ships a
 *    changed service worker.
 *  - `version.json` is written beside the bundle and never cached by the service worker, so it
 *    always reports what is deployed.
 *  - The app checks it on open, when brought to the foreground, and on pull-to-refresh. When the
 *    deployed version differs, it asks the service worker to update and reloads once the new one
 *    takes control.
 *  - A reload never interrupts a request on screen: it waits until the card clears.
 *  - One reload per target version per session. If the app is still behind after it, it stops and
 *    says so rather than reloading into the same cached bundle forever.
 */

declare const __GATE_VERSION__: string;

/** The version this bundle was built as ("dev" when bundled without build.mjs, e.g. in tests). */
export const VERSION: string = typeof __GATE_VERSION__ === "string" ? __GATE_VERSION__ : "dev";

/** Pixels of pull past the top before a release triggers an update check. */
export const PULL_THRESHOLD = 70;

/** True when the deployed version is known and differs from the running one. */
export function updateAvailable(running: string, deployed: unknown): boolean {
  if (typeof deployed !== "string" || !deployed.trim()) return false;
  if (running === "dev") return false;
  return deployed.trim() !== running;
}

export type PullPhase = "idle" | "pulling" | "armed";

/**
 * Pull-to-refresh state from the drag distance. Only a pull that started at the top of the page
 * counts; anything else stays idle so ordinary scrolling is untouched.
 */
export function pullPhase(startedAtTop: boolean, dy: number): PullPhase {
  if (!startedAtTop || dy <= 0) return "idle";
  return dy >= PULL_THRESHOLD ? "armed" : "pulling";
}

/** sessionStorage key: the version the last update-reload was aiming for. */
export const UPDATE_TARGET_KEY = "vowed-gate-update-target";

export type UpdateDecision = "current" | "update" | "blocked";

/**
 * What to do given the running and deployed versions and the version the previous update-reload in
 * this session aimed for. A second reload toward the same target is refused: it would only serve
 * the same cached bundle again.
 */
export function updateDecision(running: string, deployed: unknown, lastTarget: string | null): UpdateDecision {
  if (!updateAvailable(running, deployed)) return "current";
  return lastTarget !== null && lastTarget === String(deployed).trim() ? "blocked" : "update";
}

const session = {
  get(): string | null {
    try { return sessionStorage.getItem(UPDATE_TARGET_KEY); } catch { return null; }
  },
  set(v: string | null): void {
    try {
      if (v === null) sessionStorage.removeItem(UPDATE_TARGET_KEY);
      else sessionStorage.setItem(UPDATE_TARGET_KEY, v);
    } catch { /* storage unavailable: the breaker degrades to one check per open */ }
  },
};

/** Read the deployed version, bypassing every cache. Null when offline or unreadable. */
export async function fetchDeployedVersion(fetchFn: typeof fetch = fetch): Promise<string | null> {
  try {
    const r = await fetchFn(`version.json?t=${Date.now()}`, { cache: "no-store" });
    if (!r.ok) return null;
    const body = (await r.json()) as { version?: unknown };
    return typeof body.version === "string" ? body.version : null;
  } catch {
    return null;
  }
}

export interface UpdaterOptions {
  /** False while a request card is on screen or a decision is in flight. */
  canReload: () => boolean;
  /** Short status line for the operator ("" clears it). */
  setStatus: (text: string) => void;
}

/** DOM + service-worker wiring. Call once from boot(). */
export function installUpdater(opts: UpdaterOptions): {
  check: (manual: boolean) => Promise<void>;
  flush: () => void;
} {
  let reloadPending = false;
  let checking = false;

  const reloadWhenSafe = () => {
    if (opts.canReload()) {
      location.reload();
    } else {
      reloadPending = true;
      opts.setStatus("Update ready — it installs when this request is finished.");
    }
  };

  // A new service worker took control: the page is still running the old bundle, so reload.
  // Skip the very first claim (no previous controller), which is not an update.
  if ("serviceWorker" in navigator) {
    let hadController = !!navigator.serviceWorker.controller;
    navigator.serviceWorker.addEventListener("controllerchange", () => {
      if (!hadController) {
        hadController = true;
        return;
      }
      reloadWhenSafe();
    });
  }

  const check = async (manual: boolean) => {
    if (checking) return;
    checking = true;
    try {
      if (reloadPending) {
        reloadWhenSafe();
        return;
      }
      if (manual) opts.setStatus("Checking for updates…");
      const deployed = await fetchDeployedVersion();
      if (deployed === null) {
        if (manual) opts.setStatus("Couldn't reach the server to check for updates.");
        return;
      }
      const decision = updateDecision(VERSION, deployed, session.get());
      if (decision === "current") {
        session.set(null);
        if (manual) opts.setStatus(`Up to date (${VERSION}).`);
        return;
      }
      if (decision === "blocked") {
        opts.setStatus(`Update to ${deployed} didn't apply. Close the app fully and reopen it.`);
        return;
      }
      session.set(deployed.trim());
      opts.setStatus(`Updating to ${deployed}…`);
      const reg = "serviceWorker" in navigator ? await navigator.serviceWorker.getRegistration() : undefined;
      if (reg) {
        await reg.update();
        // No new worker came in (sw.js unchanged but the bundle changed): reload straight away;
        // otherwise controllerchange fires once the new worker activates and reloads the page.
        if (!reg.installing && !reg.waiting) reloadWhenSafe();
      } else {
        reloadWhenSafe();
      }
    } finally {
      checking = false;
    }
  };

  // Pull-to-refresh. iOS gives an installed web app no native pull-to-refresh, so this adds one.
  const indicator = document.getElementById("pull-indicator");
  let startY: number | null = null;
  let phase: PullPhase = "idle";
  const paint = (dy: number) => {
    if (!indicator) return;
    if (phase === "idle") {
      indicator.style.height = "0px";
      indicator.textContent = "";
      return;
    }
    indicator.style.height = `${Math.min(dy, PULL_THRESHOLD + 20) * 0.6}px`;
    indicator.textContent = phase === "armed" ? "Release to check for updates" : "Pull to check for updates";
  };
  document.addEventListener(
    "touchstart",
    (e) => {
      startY = window.scrollY <= 0 && e.touches.length === 1 ? e.touches[0].clientY : null;
      phase = "idle";
    },
    { passive: true },
  );
  document.addEventListener(
    "touchmove",
    (e) => {
      if (startY === null) return;
      const dy = e.touches[0].clientY - startY;
      phase = pullPhase(true, dy);
      paint(dy);
    },
    { passive: true },
  );
  document.addEventListener("touchend", () => {
    const fire = phase === "armed";
    startY = null;
    phase = "idle";
    paint(0);
    if (fire) void check(true);
  });

  const label = document.getElementById("app-version");
  if (label) label.textContent = `Version ${VERSION}`;

  /** Apply a deferred reload once the screen is clear. Cheap: call it on every poll. */
  const flush = () => {
    if (reloadPending && opts.canReload()) location.reload();
  };

  return { check, flush };
}
